import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import moment from 'moment';
import _ from 'lodash';
import * as Yup from 'yup';

import { DefaultButton, IconButton, PrimaryButton, Separator, Stack } from '@fluentui/react';
import { Pie } from 'react-chartjs-2';
import {
    ArcElement,
    BarElement,
    Chart as ChartJS,
    Legend,
    Title,
    Tooltip
} from 'chart.js';

import { RootState } from '../../redux/application/redux-store';
import { initCriteria, clearCriteria } from '../../redux/slices/Ticket/ticketSlice';
import { PropertyAttributeTypeEnum, TicketFilteringCriteria, TicketStatusEnum } from '../../api-client';
import { TRANSLATION_KEYS } from './localization/translation-keys';
import { useReport } from './hooks/useReport';
import { useProperties } from '../Setting/SettingPropertyPage/hooks/useProperties';
import { useClassNames } from './report-page.classNames';

import { IAIApplicationLayout } from '../../common/layouts/IAIApplicationLayout';
import { IAIShimmer } from '../../common/components/IAIShimmer';
import { IAIWidget } from '../../common/components/IAIWidget';
import { IAIAccordion, IAIAccordionItem } from '../../common/components/IAIAccordion';
import { FormProvider, IAIComboBox, IAIDatePicker, IAITextField } from '../../common/components/IAIForm';

ChartJS.register(BarElement, ArcElement, Title, Tooltip, Legend);

interface FormValuesProps extends TicketFilteringCriteria {
    customAttributesVertical: {
        _k: string;
        id: string;
        areaId?: string;
        departmentId?: string;
        label: string;
        value?: any;
        options?: string[];
        type: PropertyAttributeTypeEnum;
    }[];
}

const ReportPage: React.FunctionComponent = () => {
    const {
        handleTicketReports,
        ticketReports,
        ticketReportsLoading
    } = useReport();

    const {
        reportPageContainer
    } = useClassNames();

    const {
        handleProperties,
        properties
    } = useProperties();

    //#region  Criteria Form Schema

    const formDataSchema = Yup.object().shape({
        fromDate: Yup.string(),
        toDate: Yup.string(),
        areaId: Yup.string(),
        departmentIdList: Yup.array().of(Yup.string()),
        agentIdList: Yup.array().of(Yup.string()),
        customAttributesVertical: Yup.array()
    });

    const defaultValues = React.useMemo(
        () => ({
            fromDate: undefined,
            toDate: undefined,
            areaId: '',
            departmentIdList: [],
            agentIdList: [],
            customAttributesVertical: _.map(properties, dataItem => ({
                _k: dataItem.id,
                id: dataItem.id,
                areaId: dataItem.areaId,
                departmentId: dataItem.departmentId,
                label: dataItem.label,
                value: dataItem.attributeType === PropertyAttributeTypeEnum.Date
                    ? undefined
                    : '',
                options: dataItem.options || [],
                type: dataItem.attributeType
            }))
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [properties]
    );

    const methods = useForm<FormValuesProps>({ resolver: yupResolver(formDataSchema), defaultValues });

    const {
        control,
        handleSubmit,
        reset,
        setValue,
        watch
    } = methods;

    const {
        fields,
        update,
    } = useFieldArray({
        control,
        name: 'customAttributesVertical'
    });

    useEffect(() => {
        handleProperties();
        handleTicketReports({});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        reset(defaultValues);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [properties]);

    const formValues = watch();

    useEffect(() => {
        _.forEach(formValues.customAttributesVertical, (attr, index) => {
            update(index, { ...attr, value: attr.type === PropertyAttributeTypeEnum.Date ? undefined : '' })
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formValues.areaId, formValues.departmentIdList]);

    //#endregion

    const dispatch = useDispatch();

    const _handleClear = () => {
        reset(defaultValues);
        dispatch(clearCriteria());
        handleTicketReports({});
    }

    const _handleFilter = ({ customAttributesVertical, ...data }: FormValuesProps) => {
        const customAttributesPayload = _.map(_.filter(customAttributesVertical, x => !!x.value), x => ({
            id: x.id,
            value: x.type === PropertyAttributeTypeEnum.Date
                ? moment(x.value)
                    .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
                    .format('YYYY-MM-DD')
                : x.value,
        }));

        dispatch(initCriteria({
            ...data,
            fromDate: !!data.fromDate
                ? moment(data.fromDate).endOf('day').toDate().toISOString()
                : undefined,
            toDate: !!data.toDate
                ? moment(data.toDate).endOf('day').toDate().toISOString()
                : undefined,
            properties: customAttributesPayload
        }));
        handleTicketReports({
            criteria: {
                ...data,
                fromDate: !!data.fromDate
                    ? moment(data.fromDate).endOf('day').toDate().toISOString()
                    : undefined,
                toDate: !!data.toDate
                    ? moment(data.toDate).endOf('day').toDate().toISOString()
                    : undefined,
                properties: customAttributesPayload
            }
        });
    }

    const {
        t: translate
    } = useTranslation();

    const REPORT_WIDGETS = [
        {
            description: `${ticketReports?.totalCount}`,
            title: translate(TRANSLATION_KEYS.WidgetAllHeading),
            tooltip: translate(TRANSLATION_KEYS.WidgetAllTooltip),
            to: '/ticket?status=all'
        },
        {
            description: `${ticketReports?.openCount}`,
            title: translate(TRANSLATION_KEYS.WidgetOpenHeading),
            tooltip: translate(TRANSLATION_KEYS.WidgetOpenTooltip),
            to: `/ticket?status=${TicketStatusEnum.Open}`
        },
        {
            description: `${ticketReports?.pendingCount}`,
            title: translate(TRANSLATION_KEYS.WidgetPendingHeading),
            tooltip: translate(TRANSLATION_KEYS.WidgetPendingTooltip),
            to: `/ticket?status=${TicketStatusEnum.Pending}`
        },
        {
            description: `${ticketReports?.workingCount}`,
            title: translate(TRANSLATION_KEYS.WidgetWorkingHeading),
            tooltip: translate(TRANSLATION_KEYS.WidgetWorkingTooltip),
            to: `/ticket?status=${TicketStatusEnum.Working}`
        },
        {
            description: `${ticketReports?.closedCount}`,
            title: translate(TRANSLATION_KEYS.WidgetClosedHeading),
            tooltip: translate(TRANSLATION_KEYS.WidgetClosedTooltip),
            to: `/ticket?status=${TicketStatusEnum.Closed}`
        },
        {
            description: `${ticketReports?.canceledCount}`,
            title: translate(TRANSLATION_KEYS.WidgetCanceledHeading),
            tooltip: translate(TRANSLATION_KEYS.WidgetCanceledTooltip),
            to: `/ticket?status=${TicketStatusEnum.Canceled}`
        },
        {
            description: `${ticketReports?.deletedCount}`,
            title: translate(TRANSLATION_KEYS.WidgetDeletedHeading),
            tooltip: translate(TRANSLATION_KEYS.WidgetDeletedTooltip),
            to: `/ticket?status=${TicketStatusEnum.Deleted}`
        }
    ];

    const PIE_CHART_DATASET = {
        labels: [
            translate(TRANSLATION_KEYS.ChartOpenLabel),
            translate(TRANSLATION_KEYS.ChartPendingLabel),
            translate(TRANSLATION_KEYS.ChartWorkingLabel),
            translate(TRANSLATION_KEYS.ChartClosedLabel),
            translate(TRANSLATION_KEYS.ChartCanceledLabel),
            translate(TRANSLATION_KEYS.ChartDeletedLabel),
        ],
        datasets: [
            {
                data: [
                    Math.round(ticketReports?.openCount || 0),
                    Math.round(ticketReports?.pendingCount || 0),
                    Math.round(ticketReports?.workingCount || 0),
                    Math.round(ticketReports?.closedCount || 0),
                    Math.round(ticketReports?.canceledCount || 0),
                    Math.round(ticketReports?.deletedCount || 0),
                ],
                backgroundColor: [
                    'rgba(255, 99, 132, 0.2)',
                    'rgba(54, 162, 235, 0.2)',
                    'rgba(255, 206, 86, 0.2)',
                    'rgba(75, 192, 192, 0.2)',
                    'rgba(153, 102, 255, 0.2)',
                    'rgba(255, 159, 64, 0.2)',
                ],
                borderColor: [
                    'rgba(255, 99, 132, 1)',
                    'rgba(54, 162, 235, 1)',
                    'rgba(255, 206, 86, 1)',
                    'rgba(75, 192, 192, 1)',
                    'rgba(153, 102, 255, 1)',
                    'rgba(255, 159, 64, 1)',
                ],
                borderWidth: 1
            }
        ]
    };

    const iaiSystem = useSelector((state: RootState) => state.system);

    const reportDataLoading = ticketReportsLoading ||
        !iaiSystem.departmentsInitialized ||
        !iaiSystem.agentsInitialized;

    return (
        <IAIApplicationLayout
            icon={'LineChart'}
            heading={translate(TRANSLATION_KEYS.Heading)}
            secondaryHeading={translate(TRANSLATION_KEYS.SecondaryHeading)}
        >
            <div className={`report-page-container ${reportPageContainer}`}>
                <IAIAccordion>
                    <IAIAccordionItem heading={translate(TRANSLATION_KEYS.FiltersHeading)}>
                        <FormProvider methods={methods}>
                            <div className="row">
                                <div className="col-12 col-md-3">
                                    <Stack horizontal verticalAlign="end" tokens={{ childrenGap: 5 }}>
                                        <Stack.Item grow>
                                            <IAIDatePicker
                                                name="fromDate"
                                                label={translate(TRANSLATION_KEYS.FromDateLabel)}
                                                placeholder={translate(TRANSLATION_KEYS.FromDatePlaceholder)}
                                                disabled={reportDataLoading}
                                            />
                                        </Stack.Item>

                                        <IconButton
                                            iconProps={{ iconName: 'Delete' }}
                                            aria-label="Clear the date input"
                                            styles={{ root: { marginBottom: 5 } }}
                                            onClick={() => setValue("fromDate", undefined)}
                                        />
                                    </Stack>
                                </div>
                                <div className="col-12 col-md-3">
                                    <Stack horizontal verticalAlign="end" tokens={{ childrenGap: 5 }}>
                                        <Stack.Item grow>
                                            <IAIDatePicker
                                                name="toDate"
                                                label={translate(TRANSLATION_KEYS.ToDateLabel)}
                                                placeholder={translate(TRANSLATION_KEYS.ToDatePlaceholder)}
                                                disabled={reportDataLoading}
                                            />
                                        </Stack.Item>

                                        <IconButton
                                            iconProps={{ iconName: 'Delete' }}
                                            aria-label="Clear the date input"
                                            styles={{ root: { marginBottom: 5 } }}
                                            onClick={() => setValue("toDate", undefined)}
                                        />
                                    </Stack>
                                </div>
                            </div>
                            <div className="row" style={{ marginBottom: 5 }}>
                                <div className="col-12 col-md-3">
                                    <Stack horizontal verticalAlign="end" tokens={{ childrenGap: 5 }}>
                                        <Stack.Item grow>
                                            <IAIComboBox
                                                name="areaId"
                                                label={translate(TRANSLATION_KEYS.AreaLabel)}
                                                placeholder={translate(TRANSLATION_KEYS.AreaPlaceholder)}
                                                allowFreeform={true}
                                                autoComplete="on"
                                                disabled={reportDataLoading}
                                                options={_.map(iaiSystem.areas, x => ({ key: x.id!, text: x.displayName! }))}
                                            />
                                        </Stack.Item>

                                        <IconButton
                                            iconProps={{ iconName: 'Delete' }}
                                            aria-label="Clear the area input"
                                            onClick={() => setValue("areaId", '')}
                                        />
                                    </Stack>
                                </div>
                                <div className="col-12 col-md-3">
                                    <Stack horizontal verticalAlign="end" tokens={{ childrenGap: 5 }}>
                                        <Stack.Item grow>
                                            <IAIComboBox
                                                name="departmentIdList"
                                                label={translate(TRANSLATION_KEYS.DepartmentLabel)}
                                                placeholder={translate(TRANSLATION_KEYS.DepartmentPlaceholder)}
                                                allowFreeform={true}
                                                autoComplete="on"
                                                disabled={reportDataLoading}
                                                options={_.map(iaiSystem.departments, x => ({ key: x.id!, text: x.displayName! }))}
                                                multiSelect
                                            />
                                        </Stack.Item>

                                        <IconButton
                                            iconProps={{ iconName: 'Delete' }}
                                            aria-label="Clear the topic input"
                                            onClick={() => setValue("departmentIdList", [])}
                                        />
                                    </Stack>
                                </div>
                                <div className="col-12 col-md-3">
                                    <Stack horizontal verticalAlign="end" tokens={{ childrenGap: 5 }}>
                                        <Stack.Item grow>
                                            <IAIComboBox
                                                name="agentIdList"
                                                label={translate(TRANSLATION_KEYS.AgentLabel)}
                                                placeholder={translate(TRANSLATION_KEYS.AgentPlaceholder)}
                                                allowFreeform={true}
                                                autoComplete="on"
                                                disabled={reportDataLoading}
                                                options={_.map(iaiSystem.agents, x => ({ key: x.id!, text: x.displayName! }))}
                                                multiSelect
                                            />
                                        </Stack.Item>

                                        <IconButton
                                            iconProps={{ iconName: 'Delete' }}
                                            aria-label="Clear the agent input"
                                            onClick={() => setValue("agentIdList", [])}
                                        />
                                    </Stack>
                                </div>
                            </div>

                            {!!fields.length && (
                                <div className="row">
                                    {_.map(fields, (dataItem, dataIndex) => {
                                        const isVisible = !!formValues.departmentIdList &&
                                            formValues.departmentIdList.indexOf(dataItem.departmentId ?? "") !== -1;

                                        if (isVisible) {
                                            return (
                                                <div key={dataItem.id} className="col-12 col-md-3">
                                                    {dataItem.type === PropertyAttributeTypeEnum.Combo && (
                                                        <Stack horizontal verticalAlign="end" tokens={{ childrenGap: 5 }}>
                                                            <Stack.Item grow>
                                                                <IAIComboBox
                                                                    name={`customAttributesVertical[${dataIndex}].value`}
                                                                    label={dataItem.label}
                                                                    placeholder={translate(TRANSLATION_KEYS.ComboPlaceholder)}
                                                                    allowFreeform={true}
                                                                    autoComplete="on"
                                                                    disabled={reportDataLoading}
                                                                    options={_.map(dataItem.options, x => ({ key: x, text: x }))}
                                                                />
                                                            </Stack.Item>

                                                            <IconButton
                                                                iconProps={{ iconName: 'Delete' }}
                                                                aria-label="Clear the agent input"
                                                                onClick={() => {
                                                                    const attr = _.find(formValues.customAttributesVertical, x => x.id == dataItem._k);
                                                                    if (!!attr)
                                                                        update(dataIndex, { ...attr, value: '' })
                                                                }}
                                                            />
                                                        </Stack>
                                                    )}
                                                    {dataItem.type === PropertyAttributeTypeEnum.Date && (
                                                        <Stack horizontal verticalAlign="end" tokens={{ childrenGap: 5 }}>
                                                            <Stack.Item grow>
                                                                <IAIDatePicker
                                                                    name={`customAttributesVertical[${dataIndex}].value`}
                                                                    label={dataItem.label}
                                                                    placeholder={translate(TRANSLATION_KEYS.DatePlaceholder)}
                                                                    disabled={reportDataLoading}
                                                                />
                                                            </Stack.Item>

                                                            <IconButton
                                                                iconProps={{ iconName: 'Delete' }}
                                                                aria-label="Clear the agent input"
                                                                styles={{ root: { marginBottom: 5 } }}
                                                                onClick={() => {
                                                                    const attr = _.find(formValues.customAttributesVertical, x => x.id == dataItem._k);
                                                                    if (!!attr)
                                                                        update(dataIndex, { ...attr, value: '' })
                                                                }}
                                                            />
                                                        </Stack>
                                                    )}
                                                    {dataItem.type === PropertyAttributeTypeEnum.Input && (
                                                        <Stack horizontal verticalAlign="end" tokens={{ childrenGap: 5 }}>
                                                            <Stack.Item grow>
                                                                <IAITextField
                                                                    name={`customAttributesVertical[${dataIndex}].value`}
                                                                    label={dataItem.label}
                                                                    placeholder={translate(TRANSLATION_KEYS.InputPlaceholder)}
                                                                    disabled={reportDataLoading}
                                                                />
                                                            </Stack.Item>

                                                            <IconButton
                                                                iconProps={{ iconName: 'Delete' }}
                                                                aria-label="Clear the agent input"
                                                                onClick={() => {
                                                                    const attr = _.find(formValues.customAttributesVertical, x => x.id == dataItem._k);
                                                                    if (!!attr)
                                                                        update(dataIndex, { ...attr, value: '' })
                                                                }}
                                                            />
                                                        </Stack>
                                                    )}
                                                </div>
                                            );
                                        }
                                    })}
                                </div>
                            )}

                            <Separator />

                            <Stack horizontal tokens={{ childrenGap: 15 }}>
                                <DefaultButton
                                    disabled={reportDataLoading}
                                    iconProps={{ iconName: 'Cancel' }}
                                    onClick={_handleClear}
                                >
                                    {translate(TRANSLATION_KEYS.ClearText)}
                                </DefaultButton>
                                <PrimaryButton
                                    type="submit"
                                    disabled={reportDataLoading}
                                    iconProps={{ iconName: 'CheckMark' }}
                                    onClick={handleSubmit(_handleFilter)}
                                >
                                    {translate(TRANSLATION_KEYS.ApplyText)}
                                </PrimaryButton>
                            </Stack>
                        </FormProvider>
                    </IAIAccordionItem>
                </IAIAccordion>

                <Separator className="my-3" />

                <div className="row">
                    <div className="col-12 col-md-8 mx-auto">
                        <Stack horizontal horizontalAlign="center" tokens={{ childrenGap: 30 }} wrap>
                            {_.map(REPORT_WIDGETS, (dataItem, dataIndex) => (
                                <IAIShimmer
                                    key={dataIndex}
                                    type={'shimmer-widget'}
                                    loading={!ticketReports || ticketReportsLoading}
                                >
                                    <IAIWidget {...dataItem} />
                                </IAIShimmer>
                            ))}
                        </Stack>
                    </div>
                </div>

                <Separator className="my-3" />

                <Stack
                    horizontal
                    horizontalAlign="center"
                >
                    <Pie data={PIE_CHART_DATASET} style={{ maxHeight: '400px' }} />
                </Stack>
            </div>
        </IAIApplicationLayout>
    );
}

export default ReportPage;